/**
 * @fileoverview Confirm modal for Migrate Entire Department.
 * @module system-web/components/migration/DepartmentWideConfirmModal
 *
 * Responsibilities:
 * - List frozen occupancy adapters before a global apply/dry-run.
 *
 * Architecture: presentation-only. Parent owns submit via legacyMigrationService.startRun.
 *
 * @component DepartmentWideConfirmModal
 * Purpose: Warn before department-wide occupancy migration.
 * Props: mode, adapters, isSubmitting, error, onConfirm, onCancel
 * Behavior: Confirm posts datasetKey department-wide with selection ['*'].
 * Data Source: adapter names from parent (ADAPTER_META / fetchDatasets)
 * Constraints: no fabricated record counts
 * Performance: N/A
 */

import React, { useEffect, useState } from 'react';
import { AlertTriangle, Loader2, XCircle, X } from 'lucide-react';

export interface DepartmentWideAdapterLine {
  key: string;
  displayName: string;
}

export function DepartmentWideConfirmModal({
  mode,
  adapters,
  estimateAvailable,
  estimateError,
  estimateLoading,
  isSubmitting,
  error,
  onConfirm,
  onCancel,
}: {
  mode: 'dry-run' | 'apply';
  adapters: DepartmentWideAdapterLine[];
  estimateAvailable?: boolean | null;
  estimateError?: string | null;
  estimateLoading?: boolean;
  isSubmitting: boolean;
  error: string | null;
  onConfirm: (fieldResponseWindowMonths: number) => void;
  onCancel: () => void;
}) {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onCancel]);

  const [windowMonths, setWindowMonths] = useState(6);

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100]"
      onClick={onCancel}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="bg-white rounded-xl shadow-xl p-6 max-w-lg w-full mx-4 max-h-[80vh] flex flex-col relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onCancel}
          aria-label="Close dialog"
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X className="h-5 w-5" />
        </button>
        <h2 className="text-lg font-semibold mb-2 pr-6">
          {mode === 'dry-run' ? 'Preview entire department' : 'Migrate entire department'}
        </h2>
        <p className="text-sm text-gray-600 mb-3">
          {mode === 'dry-run'
            ? 'Dry-run every dataset in order (occupancy, inspection, and lookup data). No tenant data is written. Blob copy is not started.'
            : 'This writes every legacy dataset for the whole department: occupancies, contacts, systems, tanks, chemicals, hydrants, lookups, inspections (cases, responses, violations, templates, accounts, request logs), and document metadata. File bytes transfer in the background after Documents. Re-running upserts; it does not undo.'}
        </p>
        <div className="flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3">
          <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600 mt-0.5" />
          <span>Each step uses selection *. Grid checkboxes on the current tab are ignored.</span>
        </div>
        {estimateLoading && (
          <p className="text-xs text-gray-500 mb-2 inline-flex items-center gap-1">
            <Loader2 className="h-3 w-3 animate-spin" /> Checking legacy SQL…
          </p>
        )}
        {(estimateAvailable === false || estimateError) && (
          <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-2">
            {estimateError || 'Legacy SQL not available'}
          </p>
        )}
        <label className="block text-xs text-gray-700 mb-3">
          <span className="font-medium">Inspection field response window</span>
          <select
            aria-label="Inspection field response window"
            value={windowMonths}
            onChange={(e) => setWindowMonths(Number(e.target.value))}
            className="mt-1 block w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm"
          >
            <option value={1}>Past 1 month, plus the latest inspection series for each property</option>
            <option value={3}>Past 3 months, plus the latest inspection series for each property</option>
            <option value={6}>Past 6 months, plus the latest inspection series for each property</option>
            <option value={12}>Past 12 months, plus the latest inspection series for each property</option>
            <option value={36}>Past 3 years, plus the latest inspection series for each property</option>
            <option value={60}>Past 5 years, plus the latest inspection series for each property</option>
            <option value={96}>Past 8 years, plus the latest inspection series for each property</option>
            <option value={0}>All inspection field responses, no date cutoff</option>
          </select>
          <span className="mt-1 block text-[11px] text-gray-500">
            Older answers in a selected series are still included. All field responses skips the date cutoff. Applies to this run only.
          </span>
        </label>
        <ul className="text-xs text-gray-700 border rounded-lg divide-y divide-gray-100 overflow-y-auto mb-4 flex-1 min-h-0">
          {adapters.map((a) => (
            <li key={a.key} className="px-3 py-1.5">
              <span>{a.displayName}</span>
            </li>
          ))}
        </ul>
        {error && (
          <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2 mb-4">
            <XCircle className="h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
        )}
        <div className="flex gap-2 justify-end">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm border rounded-lg hover:bg-gray-50"
          >
            {isSubmitting ? 'Close' : 'Cancel'}
          </button>
          <button
            type="button"
            onClick={() => onConfirm(windowMonths)}
            disabled={isSubmitting || estimateAvailable === false}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm text-white bg-orange-600 hover:bg-orange-700 rounded-lg disabled:opacity-50"
          >
            {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
            {mode === 'dry-run' ? 'Run preview' : 'Migrate department'}
          </button>
        </div>
      </div>
    </div>
  );
}
